/* ═══════════════════════════════════════════════════════════
   nb-reveal.js
   Global scroll-reveal utility for nuviebaby warm-elite theme.

   Adds `.nb-revealed` to any `.nb-reveal` element as it scrolls
   into view. CSS in nb-global-overrides.css handles the actual
   animation (opacity 0 → 1, translateY(20px) → 0, 0.6s).

   Features:
   - IntersectionObserver (feature-detected; fallback reveals all)
   - Re-scans on DOMContentLoaded
   - Re-scans on shopify:section:load (theme editor dynamic insert)
   - Re-scans on shopify:section:unload (cleanup)
   - Respects prefers-reduced-motion: reduce (reveals immediately)
   - One-shot per element (unobserve after reveal, no re-trigger)
   - Idempotent: safe to call init() multiple times
   ═══════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var REVEAL_SELECTOR = '.nb-reveal';
  var REVEALED_CLASS = 'nb-revealed';
  var OBSERVED_ATTR = 'data-nb-reveal-observed';

  var prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var supportsIO =
    'IntersectionObserver' in window &&
    'IntersectionObserverEntry' in window &&
    'intersectionRatio' in window.IntersectionObserverEntry.prototype;

  var observer = null;

  function revealNow(el) {
    el.classList.add(REVEALED_CLASS);
  }

  function revealAllImmediately(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll(REVEAL_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      revealNow(nodes[i]);
      nodes[i].setAttribute(OBSERVED_ATTR, '1');
    }
  }

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.isIntersecting) {
            revealNow(entry.target);
            observer.unobserve(entry.target);
          }
        }
      },
      {
        root: null,
        rootMargin: '0px 0px -5% 0px',
        threshold: 0.15,
      }
    );
    return observer;
  }

  function scan(root) {
    var scope = root || document;

    // If reduced motion or no IO support, reveal everything now.
    if (prefersReducedMotion || !supportsIO) {
      revealAllImmediately(scope);
      return;
    }

    var io = getObserver();
    var nodes = scope.querySelectorAll(REVEAL_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.getAttribute(OBSERVED_ATTR) === '1') continue;
      el.setAttribute(OBSERVED_ATTR, '1');

      // If already in view at init time (above-the-fold), reveal now.
      var rect = el.getBoundingClientRect();
      var viewportH = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < viewportH * 0.95 && rect.bottom > 0) {
        revealNow(el);
        continue;
      }

      io.observe(el);
    }
  }

  function init() {
    scan(document);
  }

  // Initial scan
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor: sections added / re-rendered dynamically
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target || document);
  });

  // Theme editor: clean up observation on removal
  document.addEventListener('shopify:section:unload', function (e) {
    if (!observer || !e.target) return;
    var nodes = e.target.querySelectorAll(REVEAL_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      try {
        observer.unobserve(nodes[i]);
      } catch (err) {
        /* noop */
      }
    }
  });

  // Expose a tiny manual hook in case other scripts inject content
  window.nbReveal = { scan: scan };
})();
